import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../lib/apiClient';
import { config } from '../../../config';
import type { User } from '../types';

interface ReconcileUserRequest {
  authServiceUuid: string;
}

interface ForgetUserRequest {
  authServiceUuid: string;
}

interface ResetPasswordRequest {
  authServiceUuid: string;
  password: string;
}

interface SendPasswordResetEmailRequest {
  user: User;
}

/**
 * Mutation hook for reconciling user data across datacenters
 *
 * Calls the User API to trigger asynchronous reconciliation.
 * The actual reconciliation happens via message queue.
 *
 * @returns TanStack Query mutation
 */
export const useReconcileUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ authServiceUuid }: ReconcileUserRequest) => {
      await apiClient().put(`api/users/${authServiceUuid}/reconcile`).json();
    },
    onSuccess: () => {
      // Invalidate user queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
};

export const useForgetUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ authServiceUuid }: ForgetUserRequest) => {
      await apiClient().post(`api/users/${authServiceUuid}/forget`).json();
    },
    onSuccess: () => {
      // Invalidate user queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
};

/**
 * Mutation hook for resetting a user's password
 *
 * Sets a temporary password for the user and automatically verifies their email.
 * The user will be prompted to change the password on next login.
 *
 * @returns TanStack Query mutation
 */
export const useResetPasswordMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ authServiceUuid, password }: ResetPasswordRequest) => {
      let passwordResetSuccessful = false;

      try {
        await apiClient()
          .patch(`${config.AUTH_SERVICE_URL}/c2fo-users/${authServiceUuid}/change-password/`, {
            json: {
              password,
              needsPasswordReset: true,
            },
            prefixUrl: '', // Override prefixUrl to use full URL
          })
          .json();

        passwordResetSuccessful = true;

        await apiClient().patch(`api/users/${authServiceUuid}/email/verify`).json();
      } catch (error) {
        if (passwordResetSuccessful) {
          throw new Error(
            'Password was reset successfully, but email verification failed. The user will need to verify their email separately.',
          );
        }

        throw error;
      }
    },
    onSuccess: () => {
      // Invalidate user queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
    onError: (error: Error) => {
      throw error; // Re-throw for modal handling
    },
  });
};

export const useSendPasswordResetEmailMutation = () => {
  return useMutation({
    mutationFn: async ({ user }: SendPasswordResetEmailRequest) => {
      // Send password reset email using UUID from the user object we already have
      await apiClient()
        .post(`api/users/${user.authServiceUuid}/password-reset-email`, {
          json: { emailAddress: user.emailAddress },
        })
        .json();
    },
  });
};
